import { Component, ElementRef, Input, QueryList, ViewChildren, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SegmentedOption<T = string> {
  value: T;
  label: string;
}

/**
 * Single-choice segmented control (ARIA radiogroup). Works with formControlName / ngModel.
 * Arrow keys move and select, Home/End jump to the ends — only the selected segment is tabbable.
 */
@Component({
  selector: 'app-segmented-control',
  standalone: false,
  templateUrl: './segmented-control.component.html',
  styleUrl: './segmented-control.component.scss',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SegmentedControlComponent), multi: true }],
})
export class SegmentedControlComponent implements ControlValueAccessor {
  @Input({ required: true }) options: SegmentedOption<unknown>[] = [];
  /** id of the element labelling the group */
  @Input() labelledBy?: string;
  @Input() describedBy?: string;

  @ViewChildren('segment') private segments!: QueryList<ElementRef<HTMLButtonElement>>;

  value: unknown = null;
  disabled = false;
  private onChange: (v: unknown) => void = () => { };
  private onTouched: () => void = () => { };

  writeValue(v: unknown): void { this.value = v; }
  registerOnChange(fn: (v: unknown) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  get activeIndex(): number {
    const i = this.options.findIndex(o => o.value === this.value);
    return i === -1 ? 0 : i;
  }

  select(option: SegmentedOption<unknown>): void {
    if (this.disabled || option.value === this.value) return;
    this.value = option.value;
    this.onChange(option.value);
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    const last = this.options.length - 1;
    let next: number;
    switch (event.key) {
      case 'ArrowRight': case 'ArrowDown': next = index === last ? 0 : index + 1; break;
      case 'ArrowLeft': case 'ArrowUp': next = index === 0 ? last : index - 1; break;
      case 'Home': next = 0; break;
      case 'End': next = last; break;
      default: return;
    }
    event.preventDefault();
    this.select(this.options[next]);
    this.segments.get(next)?.nativeElement.focus();
  }

  onBlur(): void { this.onTouched(); }
}
